import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { asUTCEpoch, ConfirmDialogService, isSomething, Rental, selectAllRentals, Toaster } from '@tokeet/tv3-platform'
import * as R from 'ramda'
import * as moment from 'moment'
import { BookingFee, CreateBookingFeePayload, FeeModalities, UpdateBookingFeePayload } from '../../store/booking-fee'
import { BookingFeesService } from '../../store/booking-fees.service'
import { select, Store } from '@ngrx/store'
import { catchError, flatMap, mapTo, mergeMap, switchMap, toArray, map } from 'rxjs/operators'
import { EMPTY, from, of, throwError } from 'rxjs'
import { FeeTypes } from '@tokeet/cost-resolver'

@Component({
  selector: 'app-booking-fees',
  templateUrl: './booking-fees.component.html',
  styleUrls: ['./booking-fees.component.scss'],
})
export class BookingFeesComponent implements OnInit {
  @Input() rental: Rental

  @Output() close = new EventEmitter()

  processing = false

  fees = []

  defaultFee = {
    name: '',
    description: '',
    amount: null,
    type: FeeTypes.Flat,
    modality: FeeModalities.PerStay,
    channel_id: '',
    start: null,
    end: null,
    dow: [],
    refundable: null,
    taxable: null,
    status: true,
    guest_threshold: null,
    length_threshold: null,
    upfront: false,
  }

  rentalFees: BookingFee[] = []

  rentals$ = this.store.pipe(
    select(selectAllRentals),
    map((rentals) => R.reject((r) => r.id === this.rental?.id, rentals))
  )
  rentalCtrl = new FormControl()

  currencyCode: string

  constructor(
    private bookingFeesService: BookingFeesService,
    private fb: FormBuilder,
    private confirmDialog: ConfirmDialogService,
    private cd: ChangeDetectorRef,
    private store: Store<any>,
    private toaster: Toaster
  ) {}

  ngOnInit() {
    this.bookingFeesService.getAllFees({ rental_id: this.rental.id }).subscribe((fees) => {
      this.rentalFees = fees.sort((f) => -f.created)
      this.initFees(this.rentalFees)
    })

    this.currencyCode = R.pathOr('USD', ['currency', 'code'], this.rental)
  }

  defineForm() {
    return this.fb.group({
      pkey: [''],
      name: ['', [Validators.required]],
      description: [''],
      amount: ['', [Validators.required, Validators.min(1), Validators.max(100)]],
      modality: ['ps', [Validators.required]],
      channel_id: [],
      type: ['pct', [Validators.required]],
      dow: [[]],
      start: [null],
      end: [null],
      status: [true],
      refundable: [''],
      taxable: [''],
      guest_threshold: [''],
      length_threshold: [''],
      upfront: [false],
    })
  }

  initFees(fees: BookingFee[]) {
    if (R.isEmpty(fees)) {
      const form = this.defineForm()
      form.patchValue({
        ...this.defaultFee,
      })
      this.fees.push(form)
    }

    R.forEach((fee) => {
      const form = this.defineForm()
      let start = null
      if (isSomething(fee.scheduling) && isSomething(fee.scheduling.start)) {
        start = moment
          .utc(fee.scheduling.start * 1000)
          .startOf('date')
          .add(12, 'hours')
          .toDate()
      }

      let end = null
      if (isSomething(fee.scheduling) && isSomething(fee.scheduling.end)) {
        end = moment
          .utc(fee.scheduling.end * 1000)
          .startOf('date')
          .add(12, 'hours')
          .toDate()
      }
      form.patchValue({
        ...fee,
        status: fee.status === 1,
        upfront: fee.upfront === 1,
        start,
        end,
        dow: (fee.scheduling?.dow || []).map((d) => d?.toString()) || [],
      })
      this.fees.push(form)
    }, fees)
  }

  onAdd() {
    const form = this.defineForm()
    form.patchValue({
      ...this.defaultFee,
    })
    this.fees.push(form)
  }

  async onRemove(index) {
    this.confirmDialog
      .confirm({
        title: $localize`Delete this booking fee?`,
        body: $localize`Are you sure you want to delete this booking fee?`,
      })
      .pipe(
        switchMap(() => {
          const pkey = this.fees[index]?.value?.pkey
          if (pkey) {
            return this.bookingFeesService.deleteFee(pkey).pipe(mapTo(true))
          } else {
            return of(false)
          }
        })
      )
      .subscribe((toaster) => {
        this.fees = R.remove(index, 1, this.fees)
        if (toaster) {
          this.toaster.success($localize`Fee removed successfully`)
        }
      })
  }

  onCopy(rental: Rental) {
    this.confirmDialog
      .confirm({
        title: $localize`Overwrite current fees?`,
        body: $localize`Copying fees from different rental will overwrite current fees.`,
      })
      .pipe(
        switchMap(() => {
          const storedFees = this.fees.filter((fee) => isSomething(fee.value.pkey))
          if (R.isEmpty(storedFees)) {
            return of(true)
          } else {
            return of(storedFees).pipe(
              flatMap((fee) => from(fee)),
              mergeMap((fee) => this.bookingFeesService.deleteFee(fee.value.pkey).pipe(catchError(() => EMPTY))),
              toArray()
            )
          }
        }),
        switchMap(() => this.bookingFeesService.getAllFees({ rental_id: rental.id }))
      )
      .subscribe((fees) => {
        this.fees = []
        const sortedFees = R.sort((f) => -f.created, fees)
        const withoutIds = R.map((f) => ({ ...f, pkey: null }), sortedFees)
        this.initFees(withoutIds)
      })
  }

  toPayload(fee, rental: Rental) {
    const start = moment
      .utc(asUTCEpoch(fee.start) * 1000)
      .startOf('date')
      .add(12, 'hours')
      .unix()
    const end = moment
      .utc(asUTCEpoch(fee.end) * 1000)
      .startOf('date')
      .add(12, 'hours')
      .unix()

    const data: Partial<BookingFee> = {
      account: rental.account,
      name: fee.name,
      description: fee.description,
      rental_id: rental.id,
      amount: fee.amount,
      type: fee.type,
      modality: fee.modality,
      refundable: fee.refundable ? 1 : 0,
      taxable: fee.taxable ? 1 : 0,
      channel_id: fee.channel_id,
      guest_threshold: fee.guest_threshold,
      length_threshold: fee.length_threshold,
      status: fee.status ? 1 : 0,
      upfront: fee.upfront ? 1 : 0,
      scheduling: {
        start: isSomething(fee.start) ? start : null,
        end: isSomething(fee.end) ? end : null,
        dow: fee.dow,
      },
    }

    return {
      id: fee.pkey,
      data,
    }
  }

  isAnyInvalid(forms: FormGroup[]) {
    return R.any((f) => f.invalid, forms)
  }

  async save() {
    if (this.isAnyInvalid(this.fees)) {
      R.forEach((form) => {
        form.markAllAsTouched()
      }, this.fees)
      this.toaster.error($localize`Please fill in all required fields`)
      return
    }
    this.processing = true
    const fees: Partial<BookingFee>[] = this.fees.map((f) => f.getRawValue())
    if (R.isEmpty(fees)) {
      for await (const fee of this.rentalFees) {
        try {
          await this.bookingFeesService.deleteFee(fee.pkey).toPromise()
        } catch (e) {
          console.log(`error - ${JSON.stringify(e)}`)
        }
      }
      this.toaster.success($localize`Booking fees saved successfully`)
      this.close.emit(true)
      return
    }

    of(fees)
      .pipe(
        flatMap((fee) => from(fee)),
        mergeMap((fee) => {
          const payload = this.toPayload(fee, this.rental)
          if (payload.id) {
            return this.bookingFeesService.updateFee(payload.id, payload.data as UpdateBookingFeePayload).pipe(
              catchError((error) => {
                this.toaster.error(null, 'Error', error)
                this.processing = false
                return throwError(error)
              })
            )
          } else {
            return this.bookingFeesService.createFee(payload.data as CreateBookingFeePayload).pipe(
              catchError((error) => {
                this.toaster.error(null, 'Error', error)
                this.processing = false
                return throwError(error)
              })
            )
          }
        }),
        toArray()
      )
      .subscribe(() => {
        this.toaster.success($localize`Booking fees saved successfully`)
        this.close.emit(true)
      })
  }
}
